import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, BookOpen, ClipboardList, Zap, BarChart2,
  Calendar, Repeat, CheckSquare, GitBranch, FileText,
  Clock, User, Shield, Tag, ExternalLink, Edit,
  ChevronDown, ChevronUp, Star, AlertCircle,
  GitCommit, Award
} from 'lucide-react';
import { VUKZ, VUKZ_GRADIENT } from '../../theme/vukzTheme';
import PageLayout from '../../components/layout/PageLayout';
import {
  getLibraryItem,
  submitForReview,
  approveItem,
  rejectItem,
  archiveItem,
  ITEM_TYPES,
  ITEM_STATUSES,
  EVIDENCE_LEVELS,
  SPORT_LABELS,
} from '../../services/libraryService';
import { useRBAC } from '../../contexts/RBACContext';

// ─── Icon map ────────────────────────────────────────────────────────
const ICON_MAP = {
  'clipboard-list': ClipboardList,
  'zap':            Zap,
  'bar-chart-2':    BarChart2,
  'book-open':      BookOpen,
  'calendar':       Calendar,
  'repeat':         Repeat,
  'check-square':   CheckSquare,
  'git-branch':     GitBranch,
};

// ─── Status Badge ────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const meta = ITEM_STATUSES[status?.toLowerCase()] || ITEM_STATUSES.draft;
  return (
    <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border"
         style={{ borderColor: VUKZ.border }}>
      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: meta.dotColor }} />
      <span className="text-xs font-bold" style={{ color: VUKZ.text }}>{meta.label}</span>
    </div>
  );
};

// ─── Evidence Badge ──────────────────────────────────────────────────
const EvidenceBadge = ({ level }) => {
  const meta = EVIDENCE_LEVELS[level];
  if (!meta) return null;
  return (
    <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full"
         style={{ backgroundColor: VUKZ.primaryLight }}>
      <Award className="w-3.5 h-3.5" style={{ color: meta.color }} />
      <span className="text-xs font-bold" style={{ color: meta.color }}>{meta.label}</span>
    </div>
  );
};

// ─── Metadata Row ────────────────────────────────────────────────────
const MetaRow = ({ icon: Icon, label, value }) => {
  if (!value) return null;
  return (
    <div className="flex items-center gap-3 py-2.5 border-b" style={{ borderColor: VUKZ.border }}>
      <Icon className="w-4 h-4 flex-shrink-0" style={{ color: VUKZ.textLight }} />
      <span className="text-sm font-medium" style={{ color: VUKZ.textLight }}>{label}</span>
      <span className="text-sm font-semibold ml-auto" style={{ color: VUKZ.text }}>{value}</span>
    </div>
  );
};

// ─── Content Markdown Viewer ─────────────────────────────────────────
const renderInline = (text) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} className="font-bold">{part.slice(2, -2)}</strong>;
    }
    return part;
  });
};

const MarkdownBlock = ({ text }) => {
  const lines = text.split('\n');
  return lines.map((line, idx) => {
    // Headings
    if (line.startsWith('### '))
      return <h4 key={idx} className="text-sm font-bold mt-4 mb-1" style={{ color: VUKZ.text }}>{line.slice(4)}</h4>;
    if (line.startsWith('## '))
      return <h3 key={idx} className="text-base font-bold mt-5 mb-2" style={{ color: VUKZ.primary }}>{line.slice(3)}</h3>;
    if (line.startsWith('# '))
      return <h2 key={idx} className="text-lg font-black mt-6 mb-3" style={{ color: VUKZ.text }}>{line.slice(2)}</h2>;
    // HR
    if (/^---+$/.test(line))
      return <hr key={idx} className="my-4 border-t" style={{ borderColor: VUKZ.border }} />;
    // Table rows (skip separator rows)
    if (line.startsWith('|') && line.includes('|')) {
      const cells = line.split('|').filter(c => c.trim());
      if (cells.every(c => /^-+$/.test(c.trim()))) return null;
      const nextLine = lines[idx + 1] || '';
      const isHeader = /^\|[-|]+\|$/.test(nextLine.trim());
      return (
        <div key={idx} className="flex gap-4 py-1 text-xs font-mono" style={{ color: VUKZ.text }}>
          {cells.map((cell, ci) => (
            <span key={ci} className={`flex-1 ${isHeader ? 'font-bold' : ''}`}>{cell.trim()}</span>
          ))}
        </div>
      );
    }
    // Numbered list
    if (/^\d+\.\s/.test(line)) {
      const num = line.match(/^(\d+)/)[1];
      const txt = line.replace(/^\d+\.\s/, '');
      return (
        <div key={idx} className="flex gap-2 py-0.5 pl-2">
          <span className="text-xs font-bold flex-shrink-0" style={{ color: VUKZ.primary }}>{num}.</span>
          <span className="text-sm" style={{ color: VUKZ.text }}>{renderInline(txt)}</span>
        </div>
      );
    }
    // Bullet list
    if (/^\s*[-*]\s/.test(line)) {
      const indent = (line.match(/^(\s*)/)[1] || '').length;
      const txt = line.replace(/^\s*[-*]\s/, '');
      return (
        <div key={idx} className="flex gap-2 py-0.5" style={{ paddingLeft: `${12 + indent * 8}px` }}>
          <span className="text-xs mt-1 flex-shrink-0" style={{ color: VUKZ.primary }}>•</span>
          <span className="text-sm" style={{ color: VUKZ.text }}>{renderInline(txt)}</span>
        </div>
      );
    }
    // Empty line
    if (!line.trim()) return <div key={idx} className="h-2" />;
    // Paragraph
    return <p key={idx} className="text-sm leading-relaxed py-0.5" style={{ color: VUKZ.text }}>{renderInline(line)}</p>;
  });
};

const ContentViewer = ({ content }) => {
  const [expanded, setExpanded] = useState(false);

  if (!content || (typeof content === 'object' && Object.keys(content).length === 0)) {
    return (
      <div className="text-center py-8 rounded-xl border" style={{ borderColor: VUKZ.border }}>
        <FileText className="w-8 h-8 mx-auto mb-2" style={{ color: VUKZ.border }} />
        <p className="text-sm" style={{ color: VUKZ.textLight }}>
          Conteúdo ainda não adicionado
        </p>
      </div>
    );
  }

  const raw = typeof content === 'string'
    ? content
    : JSON.stringify(content, null, 2);

  const isLong = raw.length > 1500;
  const displayed = (!expanded && isLong) ? raw.slice(0, 1500) : raw;

  return (
    <div>
      <div className="bg-gray-50 rounded-xl p-5 overflow-x-auto">
        <MarkdownBlock text={displayed} />
        {!expanded && isLong && (
          <div className="mt-2 text-center">
            <span className="text-xs" style={{ color: VUKZ.textLight }}>…</span>
          </div>
        )}
      </div>
      {isLong && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-2 text-xs font-semibold flex items-center gap-1"
          style={{ color: VUKZ.primary }}
        >
          {expanded ? (
            <><ChevronUp className="w-3.5 h-3.5" /> Mostrar menos</>
          ) : (
            <><ChevronDown className="w-3.5 h-3.5" /> Mostrar tudo</>
          )}
        </button>
      )}
    </div>
  );
};

// ─── Tags ────────────────────────────────────────────────────────────
const TagsList = ({ tags }) => {
  if (!tags) return null;
  let parsed = tags;
  if (typeof tags === 'string') {
    try { parsed = JSON.parse(tags); } catch { return null; }
  }
  if (!Array.isArray(parsed) || parsed.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2">
      {parsed.map((tag, i) => (
        <Link
          key={i}
          to={`/library/search?q=${encodeURIComponent(tag)}`}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold
                     hover:opacity-80 transition-opacity"
          style={{ backgroundColor: VUKZ.primaryLight, color: VUKZ.primary }}
        >
          <Tag className="w-3 h-3" />
          {tag}
        </Link>
      ))}
    </div>
  );
};

// =====================================================================
// MAIN COMPONENT
// =====================================================================
const LibraryItemDetailPage = () => {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const { hasRole } = useRBAC();

  const [item, setItem]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const isGestor    = hasRole('GESTOR');
  const isTreinador = hasRole('TREINADOR');

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getLibraryItem(itemId);
        if (!cancelled) {
          if (data) {
            setItem(data);
          } else {
            setError('Item não encontrado');
          }
        }
      } catch (err) {
        if (!cancelled) setError('Erro ao carregar item');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [itemId]);

  // ── Actions ────────────────────────────────────────────────────────
  const doAction = async (fn, ...args) => {
    setActionLoading(true);
    try {
      await fn(...args);
      const updated = await getLibraryItem(itemId);
      setItem(updated);
    } catch (err) {
      console.error('[Detail] action error:', err);
    } finally {
      setActionLoading(false);
    }
  };

  // ── Loading / Error ────────────────────────────────────────────────
  if (loading) {
    return (
      <PageLayout header={{ title: 'Carregando...' }}>
        <div className="space-y-4">
          <div className="h-8 w-48 rounded-lg animate-pulse" style={{ backgroundColor: VUKZ.primaryLight }} />
          <div className="h-64 rounded-2xl animate-pulse" style={{ backgroundColor: VUKZ.primaryLight }} />
          <div className="h-40 rounded-2xl animate-pulse" style={{ backgroundColor: VUKZ.primaryLight }} />
        </div>
      </PageLayout>
    );
  }

  if (error || !item) {
    return (
      <PageLayout header={{ title: 'Biblioteca Digital' }}>
        <div className="text-center py-20">
          <AlertCircle className="w-12 h-12 mx-auto mb-4" style={{ color: VUKZ.border }} />
          <h3 className="text-lg font-bold mb-2" style={{ color: VUKZ.text }}>
            {error || 'Item não encontrado'}
          </h3>
          <Link
            to="/library"
            className="inline-flex items-center gap-2 mt-4 text-sm font-semibold"
            style={{ color: VUKZ.primary }}
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar à biblioteca
          </Link>
        </div>
      </PageLayout>
    );
  }

  const typeMeta = ITEM_TYPES[item.item_type?.toLowerCase()] || ITEM_TYPES.protocol;
  const TypeIcon = ICON_MAP[typeMeta.icon] || FileText;
  const status = item.status?.toLowerCase();

  return (
    <PageLayout
      header={{
        title: 'Biblioteca Digital',
        subtitle: typeMeta.label,
        actions: (
          <Link
            to="/library"
            className="inline-flex items-center gap-2 text-sm font-semibold"
            style={{ color: VUKZ.primary }}
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar
          </Link>
        ),
      }}
    >
      <div className="max-w-4xl mx-auto space-y-6">

        {/* ─── Header Card ──────────────────────────────────────── */}
        <div className="bg-white rounded-2xl border overflow-hidden"
             style={{ borderColor: VUKZ.border }}>
          {/* Banner */}
          <div className="h-20 flex items-center px-6" style={{ background: VUKZ_GRADIENT }}>
            <TypeIcon className="w-8 h-8 text-white/40" />
          </div>

          <div className="px-6 pb-6 -mt-5">
            {/* Icon overlap */}
            <div className="w-14 h-14 rounded-xl border-4 border-white bg-white shadow-md
                            flex items-center justify-center mb-4"
                 style={{ backgroundColor: VUKZ.primaryLight }}>
              <TypeIcon className="w-7 h-7" style={{ color: VUKZ.primary }} />
            </div>

            {/* Title + badges */}
            <div className="flex flex-wrap items-start gap-3 mb-4">
              <div className="flex-1 min-w-0">
                <h1 className="text-xl md:text-2xl font-black leading-tight mb-2"
                    style={{ color: VUKZ.text }}>
                  {item.title}
                </h1>
                <div className="flex flex-wrap items-center gap-2">
                  <StatusBadge status={item.status} />
                  <EvidenceBadge level={item.evidence_level} />
                  {item.sport && (
                    <span className="text-xs font-medium px-2.5 py-1 rounded-full"
                          style={{ backgroundColor: VUKZ.primaryLight, color: VUKZ.primary }}>
                      {SPORT_LABELS[item.sport] || item.sport}
                    </span>
                  )}
                  {item.version && (
                    <span className="text-xs font-medium px-2.5 py-1 rounded-full"
                          style={{ backgroundColor: VUKZ.primaryLight, color: VUKZ.textLight }}>
                      v{item.version}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Description */}
            {item.description && (
              <p className="text-sm leading-relaxed" style={{ color: VUKZ.textLight }}>
                {item.description}
              </p>
            )}
          </div>
        </div>

        {/* ─── Two-column layout ───────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Main column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Content */}
            <div className="bg-white rounded-2xl border p-6"
                 style={{ borderColor: VUKZ.border }}>
              <h3 className="text-sm font-bold uppercase tracking-wider mb-4"
                  style={{ color: VUKZ.textLight }}>
                Conteúdo
              </h3>
              <ContentViewer content={item.content} />
            </div>

            {/* Tags */}
            {(item.tags || item.tags_json) && (
              <div className="bg-white rounded-2xl border p-6"
                   style={{ borderColor: VUKZ.border }}>
                <h3 className="text-sm font-bold uppercase tracking-wider mb-4"
                    style={{ color: VUKZ.textLight }}>
                  Tags
                </h3>
                <TagsList tags={item.tags || item.tags_json} />
              </div>
            )}

            {/* Actions bar */}
            <div className="bg-white rounded-2xl border p-6"
                 style={{ borderColor: VUKZ.border }}>
              <h3 className="text-sm font-bold uppercase tracking-wider mb-4"
                  style={{ color: VUKZ.textLight }}>
                Ações
              </h3>
              <div className="flex flex-wrap gap-3">
                {/* Submit for review */}
                {isTreinador && status === 'draft' && (
                  <button
                    onClick={() => doAction(submitForReview, item.item_id)}
                    disabled={actionLoading}
                    className="px-5 py-2.5 rounded-xl text-sm font-bold text-white
                               disabled:opacity-50 transition-all hover:opacity-90"
                    style={{ backgroundColor: VUKZ.primary }}
                  >
                    Submeter para Revisão
                  </button>
                )}

                {/* Approve / Reject */}
                {isGestor && status === 'pending_review' && (
                  <>
                    <button
                      onClick={() => doAction(approveItem, item.item_id)}
                      disabled={actionLoading}
                      className="px-5 py-2.5 rounded-xl text-sm font-bold text-white
                                 disabled:opacity-50 transition-all hover:opacity-90"
                      style={{ backgroundColor: VUKZ.success }}
                    >
                      ✓ Aprovar
                    </button>
                    <button
                      onClick={() => {
                        const reason = prompt('Motivo da rejeição:');
                        if (reason) doAction(rejectItem, item.item_id, reason);
                      }}
                      disabled={actionLoading}
                      className="px-5 py-2.5 rounded-xl text-sm font-bold border
                                 disabled:opacity-50 transition-all hover:opacity-90"
                      style={{ borderColor: VUKZ.error, color: VUKZ.error }}
                    >
                      Rejeitar
                    </button>
                  </>
                )}

                {/* Archive */}
                {(isGestor || isTreinador) && status === 'approved' && (
                  <button
                    onClick={() => doAction(archiveItem, item.item_id)}
                    disabled={actionLoading}
                    className="px-5 py-2.5 rounded-xl text-sm font-bold border
                               disabled:opacity-50 transition-all hover:opacity-90"
                    style={{ borderColor: VUKZ.border, color: VUKZ.textLight }}
                  >
                    Arquivar
                  </button>
                )}

                {/* Edit (owner, while draft) */}
                {isTreinador && status === 'draft' && (
                  <button
                    onClick={() => navigate(`/library/edit/${item.item_id}`)}
                    className="px-5 py-2.5 rounded-xl text-sm font-bold border
                               transition-all hover:opacity-90 flex items-center gap-2"
                    style={{ borderColor: VUKZ.border, color: VUKZ.text }}
                  >
                    <Edit className="w-4 h-4" />
                    Editar
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Metadata */}
            <div className="bg-white rounded-2xl border p-5"
                 style={{ borderColor: VUKZ.border }}>
              <h3 className="text-sm font-bold uppercase tracking-wider mb-3"
                  style={{ color: VUKZ.textLight }}>
                Metadados
              </h3>
              <div>
                <MetaRow icon={FileText} label="Tipo"
                         value={typeMeta.label} />
                <MetaRow icon={Star} label="Esporte"
                         value={SPORT_LABELS[item.sport_type || item.sport || item.modality] || item.sport_type || item.sport} />
                <MetaRow icon={Award} label="Evidência"
                         value={EVIDENCE_LEVELS[item.evidence_level]?.label} />
                <MetaRow icon={GitCommit} label="Versão"
                         value={item.version ? `v${item.version}` : null} />
                <MetaRow icon={BarChart2} label="Evidence Score"
                         value={item.evidence_score != null ? `${item.evidence_score}/100` : null} />
                <MetaRow icon={Shield} label="Completeness"
                         value={item.completeness_score != null ? `${item.completeness_score}%` : null} />
                <MetaRow icon={Tag} label="Categoria"
                         value={item.category} />
                <MetaRow icon={User} label="Criado por"
                         value={item.created_by_name || `ID ${item.created_by}`} />
                <MetaRow icon={Clock} label="Criado em"
                         value={item.created_at ? new Date(item.created_at).toLocaleDateString('pt-BR') : null} />
                <MetaRow icon={Clock} label="Atualizado"
                         value={item.updated_at ? new Date(item.updated_at).toLocaleDateString('pt-BR') : null} />
                {item.approved_by && (
                  <MetaRow icon={Shield} label="Aprovado por"
                           value={item.approved_by_name || `ID ${item.approved_by}`} />
                )}
                {item.approved_at && (
                  <MetaRow icon={Clock} label="Aprovado em"
                           value={new Date(item.approved_at).toLocaleDateString('pt-BR')} />
                )}
              </div>
            </div>

            {/* OneLake path (if exists) */}
            {item.onelake_path && (
              <div className="bg-white rounded-2xl border p-5"
                   style={{ borderColor: VUKZ.border }}>
                <h3 className="text-sm font-bold uppercase tracking-wider mb-3"
                    style={{ color: VUKZ.textLight }}>
                  Data Lake
                </h3>
                <div className="flex items-start gap-2">
                  <ExternalLink className="w-4 h-4 mt-0.5 flex-shrink-0"
                                style={{ color: VUKZ.textLight }} />
                  <code className="text-xs font-mono break-all leading-relaxed"
                        style={{ color: VUKZ.text }}>
                    {item.onelake_path}
                  </code>
                </div>
              </div>
            )}

            {/* Quick nav */}
            <div className="bg-white rounded-2xl border p-5"
                 style={{ borderColor: VUKZ.border }}>
              <h3 className="text-sm font-bold uppercase tracking-wider mb-3"
                  style={{ color: VUKZ.textLight }}>
                Navegação
              </h3>
              <div className="space-y-2">
                <Link to="/library"
                      className="flex items-center gap-2 text-sm font-semibold hover:underline"
                      style={{ color: VUKZ.primary }}>
                  <ArrowLeft className="w-4 h-4" /> Biblioteca
                </Link>
                <Link to="/library/methodologies"
                      className="flex items-center gap-2 text-sm font-semibold hover:underline"
                      style={{ color: VUKZ.primary }}>
                  <BookOpen className="w-4 h-4" /> Metodologias
                </Link>
                <Link to="/library/protocols"
                      className="flex items-center gap-2 text-sm font-semibold hover:underline"
                      style={{ color: VUKZ.primary }}>
                  <ClipboardList className="w-4 h-4" /> Protocolos
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default LibraryItemDetailPage;
